import React, { useEffect, useMemo } from 'react';
import { cn } from '@/lib';
import IcClose from '@/assets/icons/ic-x-close.svg';
import IcFile from '@/assets/icons/ic-file.svg';
import IcImage from '@/assets/icons/ic-image.svg';
import { FileAttachmentPreviewProps } from '../types/fileAttachments.types';
import { getFileSizeString, isImageFile } from '../constants/fileAttachments.constants';

export const FileAttachmentPreview: React.FC<FileAttachmentPreviewProps> = ({
    attachments,
    onRemoveAttachment,
    onPreviewImage,
    className = '',
    showCloseButton = true,
    size = 'md',
    isLoading = false,
}) => {
    // Memoize object URLs to prevent recreating them on every render
    // Sort so images come first, then non-image files
    const fileUrls = useMemo(() => {
        // Create array with original index
        const filesWithIndex = attachments.map((file, originalIndex) => ({
            file,
            originalIndex,
            url: isImageFile(file) ? URL.createObjectURL(file) : null,
        }));

        // Sort: images first, then non-images
        return filesWithIndex.sort((a, b) => {
            const aIsImage = isImageFile(a.file);
            const bIsImage = isImageFile(b.file);
            if (aIsImage && !bIsImage) return 1;
            if (!aIsImage && bIsImage) return -1;
            return 0; // Maintain original order within same type
        });
    }, [attachments]);

    // Cleanup object URLs only when attachments change or component unmounts
    useEffect(() => {
        // Store current URLs for cleanup
        const currentUrls = fileUrls.map(({ url }) => url).filter((url): url is string => url !== null);

        return () => {
            // Only revoke URLs on cleanup
            currentUrls.forEach(url => {
                URL.revokeObjectURL(url);
            });
        };
    }, [attachments]); // Depend on attachments, not fileUrls

    if (attachments.length === 0) {
        return null;
    }

    const getFileIcon = (file: File) => {
        if (isImageFile(file)) {
            return <IcImage className='text-canvas size-4' />;
        }
        return <IcFile className='text-canvas size-4' />;
    };

    const getSizeClasses = () => {
        switch (size) {
            case 'xs':
                return {
                    container: 'size-16',
                    card: 'max-w-3xs',
                };
            case 'sm':
                return {
                    container: 'size-16',
                    card: 'max-w-xs',
                };
            case 'lg':
                return {
                    container: 'size-20',
                    card: 'max-w-md',
                };
            default:
                return {
                    container: 'size-16',
                    card: 'max-w-sm',
                };
        }
    };

    const sizeClasses = getSizeClasses();

    return (
        <div className={cn('flex justify-start gap-2 overflow-x-auto', className)}>
            {fileUrls.map(({ file, url, originalIndex }) =>
                isImageFile(file) ? (
                    // For images, show image directly with absolute positioned close button
                    <div
                        key={`${file.name}-${file.size}-${file.lastModified}`}
                        className={cn('relative flex-shrink-0', sizeClasses.container)}
                    >
                        <img
                            src={url || ''}
                            alt={file.name}
                            className='size-full cursor-pointer rounded-lg object-cover transition-opacity hover:opacity-80'
                            onClick={() => onPreviewImage?.(file)}
                            onError={e => {
                                console.error('Failed to load image preview:', file.name);
                                // Fallback: try creating a new object URL
                                const target = e.target as HTMLImageElement;
                                if (!target.dataset.retried) {
                                    target.dataset.retried = 'true';
                                    target.src = URL.createObjectURL(file);
                                }
                            }}
                        />
                        {/* Loading overlay */}
                        {isLoading && (
                            <div className='bg-opacity-50 absolute inset-0 flex items-center justify-center rounded-lg bg-black'>
                                <div className='size-4 animate-spin rounded-full border-2 border-white border-t-transparent' />
                            </div>
                        )}
                        {/* Close Button - only show if showCloseButton is true */}
                        {showCloseButton && !isLoading && (
                            <button
                                onClick={e => {
                                    e.stopPropagation();
                                    onRemoveAttachment(originalIndex);
                                }}
                                className='bg-brand-soft text-canvas hover:bg-accent-dim absolute top-1 right-1 flex size-5 cursor-pointer items-center justify-center rounded-full transition-all'
                                aria-label='Remove attachment'
                            >
                                <IcClose className='size-3' />
                            </button>
                        )}
                    </div>
                ) : (
                    // For non-images, show card with icon and file info
                    <div
                        key={`${file.name}-${file.size}-${file.lastModified}`}
                        className={cn(
                            'border-brand-subtle bg-canvas relative flex items-center gap-3 rounded-lg border p-3 pr-5',
                            showCloseButton ? 'w-fit' : 'w-full',
                            sizeClasses.card
                        )}
                    >
                        <div className='bg-brand-soft flex size-10 flex-shrink-0 items-center justify-center rounded-lg'>
                            {getFileIcon(file)}
                        </div>

                        {/* File Information */}
                        <div className='min-w-0 flex-1'>
                            <div className='caption-1-bold text-dark truncate' title={file.name}>
                                {file.name}
                            </div>
                            <div className='caption-2-regular text-gray truncate'>
                                {file.name.split('.').pop()?.toUpperCase() || 'FILE'} • {getFileSizeString(file.size)}
                            </div>
                        </div>

                        {/* Loading indicator */}
                        {isLoading && (
                            <div className='flex size-5 items-center justify-center'>
                                <div className='border-brand-main size-3 animate-spin rounded-full border-2 border-t-transparent' />
                            </div>
                        )}

                        {/* Close Button - only show if showCloseButton is true */}
                        {showCloseButton && !isLoading && (
                            <button
                                onClick={() => onRemoveAttachment(originalIndex)}
                                className='bg-brand-soft text-canvas hover:bg-stroke-canvas absolute top-1 right-1 flex size-5 cursor-pointer items-center justify-center rounded-full transition-colors'
                                aria-label='Remove attachment'
                            >
                                <IcClose className='size-3' />
                            </button>
                        )}
                    </div>
                )
            )}
        </div>
    );
};
